import { useEffect, useState, useRef } from 'react';
import { useUpdateCallsToAgentRatio } from 'services/hooks/queries';
import debounce from 'lodash.debounce';
import { AgenticWorkflowDetails } from 'services/api/getAgenticWorkflowDetails';
import { toast } from 'react-toastify';
import styles from './ConcurrentCalls.module.scss';
import { Minus, Plus } from 'lucide-react';

type ConcurrentCallsProps = {
    workflowId: string;
    workflowDetails?: AgenticWorkflowDetails;
    isLoadingAgenticWorkflowDetails?: boolean;
};

export const ConcurrentCalls = ({
    workflowId,
    workflowDetails,
    isLoadingAgenticWorkflowDetails,
}: ConcurrentCallsProps) => {
    const [concurrentCalls, setConcurrentCalls] = useState('1');
    const { updateCallsToAgentRatio } = useUpdateCallsToAgentRatio();

    // Create debounced function only once using useRef
    const debouncedUpdateCallsToAgentRatio = useRef(
        debounce((params: { id: string; ratio: number }) => {
            updateCallsToAgentRatio(params, {
                onSuccess: (resp) => {
                    toast.success(resp.message);
                },
                onError: () => {
                    toast.error('Failed to update calls to agent ratio');
                },
            });
        }, 300)
    ).current;

    // Set concurrent calls when workflow details are loaded
    useEffect(() => {
        if (workflowDetails && !isLoadingAgenticWorkflowDetails) {
            setConcurrentCalls(workflowDetails.callsToAgentRatio.toString());
        }
    }, [workflowDetails, isLoadingAgenticWorkflowDetails]);

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedUpdateCallsToAgentRatio.cancel();
        };
    }, [debouncedUpdateCallsToAgentRatio]);

    // Handle concurrent calls change with debounce
    const handleConcurrentCallsChange = (newValue: string) => {
        // Ensure value is within reasonable limits (minimum 0, no maximum)
        const numValue = parseInt(newValue, 10);
        if (isNaN(numValue) || numValue < 0) {
            newValue = '0';
        } else {
            newValue = String(numValue);
        }

        setConcurrentCalls(newValue);

        if (workflowId) {
            debouncedUpdateCallsToAgentRatio({ id: workflowId, ratio: numValue });
        }
    };

    // Handle increment and decrement buttons
    const handleIncrement = () => {
        const currentValue = parseInt(concurrentCalls, 10);
        if (isNaN(currentValue)) {
            handleConcurrentCallsChange('5');
        } else {
            // Round up to the next multiple of 5
            const nextMultipleOf5 = Math.ceil((currentValue + 1) / 5) * 5;
            handleConcurrentCallsChange(String(nextMultipleOf5));
        }
    };

    const handleDecrement = () => {
        const currentValue = parseInt(concurrentCalls, 10);
        if (isNaN(currentValue) || currentValue <= 5) {
            handleConcurrentCallsChange('0');
        } else {
            // Round down to the previous multiple of 5
            const prevMultipleOf5 = Math.floor((currentValue - 1) / 5) * 5;
            handleConcurrentCallsChange(String(prevMultipleOf5));
        }
    };

    // Handle direct input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setConcurrentCalls(newValue);
    };

    // Handle input blur for validation
    const handleInputBlur = () => {
        handleConcurrentCallsChange(concurrentCalls);
    };

    // Handle wheel events for scrolling
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
        if (e.deltaY < 0) {
            handleIncrement();
        } else {
            handleDecrement();
        }
    };

    return (
        <div className={styles.container}>
            <span className={styles.label}>Calls Per Agent</span>
            <button
                className={styles.decrementBtn}
                disabled={isLoadingAgenticWorkflowDetails}
                onClick={handleDecrement}
                aria-label='Decrease calls per agent'
            >
                <Minus size={14} />
            </button>
            <input
                type='text'
                className={styles.input}
                value={concurrentCalls}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onWheel={handleWheel}
                onFocus={(e) => e.target.select()}
                aria-label='Calls per agent'
            />
            <button
                className={styles.incrementBtn}
                disabled={isLoadingAgenticWorkflowDetails}
                onClick={handleIncrement}
                aria-label='Increase calls per agent'
            >
                <Plus size={14} />
            </button>
        </div>
    );
};
